import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatViews } from '@/utils/article';

interface Props {
  article: Article;
  variant?: 'grid' | 'list' | 'compact';
  index?: number;
}

function HotFlag({ big }: { big?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-secondary-500 text-secondary-950 font-label font-semibold ${big ? 'text-[11px] px-2 py-0.5' : 'text-[10px] px-2 py-0.5'}`}>
      <i className="ri-fire-fill text-xs" /> 热
    </span>
  );
}

function Meta({ article }: { article: Article }) {
  return (
    <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
      <span className="flex items-center gap-1"><i className="ri-time-line" /> {article.publishedAt.slice(5, 16)}</span>
      <span className="flex items-center gap-1"><i className="ri-eye-line" /> {formatViews(article.views)}</span>
      <span className="flex items-center gap-1"><i className="ri-chat-3-line" /> {formatViews(article.comments)}</span>
    </div>
  );
}

export default function ArticleCard({ article, variant = 'grid', index }: Props) {
  if (variant === 'list') {
    return (
      <Link
        to={`/detail/${article.id}`}
        className="group flex gap-4 p-3 md:p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 transition-colors"
      >
        <div className="w-28 h-28 sm:w-40 sm:h-28 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑料网曝大瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 font-medium">{article.categoryName}</span>
            <span className="text-[11px] text-foreground-400">{article.sub}</span>
            {article.views > 250000 && <HotFlag />}
          </div>
          <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-xs md:text-sm text-foreground-500 line-clamp-2 hidden sm:block">{article.summary}</p>
          <div className="mt-auto pt-2"><Meta article={article} /></div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={`/detail/${article.id}`} className="group flex items-start gap-3">
        {typeof index === 'number' && (
          <span className="font-heading font-extrabold text-lg text-foreground-200 w-6 shrink-0 leading-tight">
            {String(index + 1).padStart(2, '0')}
          </span>
        )}
        <div className="w-20 h-16 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 黑料网曝大瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">{article.title}</h4>
          <div className="mt-1.5 flex items-center gap-2 text-[11px] text-foreground-400">
            <span>{article.publishedAt.slice(5, 10)}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line" /> {formatViews(article.views)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex flex-col bg-background-50 rounded-lg border border-background-200 overflow-hidden hover:border-primary-300 transition-colors"
    >
      <div className="relative w-full h-44 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 黑料网曝大瓜`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-500 text-background-50 font-medium">{article.categoryName}</span>
          {article.views > 250000 && <HotFlag />}
        </div>
      </div>
      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-heading font-semibold text-base text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-500 line-clamp-2">{article.summary}</p>
        <div className="mt-auto pt-3 flex items-center justify-between">
          <Meta article={article} />
        </div>
      </div>
    </Link>
  );
}